import {
    Button,
    Card,
    Col,
    Descriptions,
    Row,
    Space,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import { MdOutlineHelpOutline } from 'react-icons/md';
import { BookingType } from '../../types/booking';

interface Props {
    booking: BookingType;
    showEditPriceBreakupModal: () => void;
}

interface PriceBreakUpItemProps {
    label: string;
    value: string | number;
    helpText: string;
    tag?: boolean;
    tagColor?: string;
}

const { Text } = Typography;
const { Item } = Descriptions;

const PriceBreakUp: React.FC<Props> = ({ booking, showEditPriceBreakupModal }) => {
    const DELIVERY_PRICE_ITEMS = [
        {
            label: 'Base Fare',
            value: `$${booking.priceBreakUp.baseFare.toFixed(2) || 0.00}`,
            helpText: 'Base cost of the service',
        },
        {
            label: 'Per Minute Charge',
            value: `$${booking.priceBreakUp.perMinCharge.toFixed(2) || 0.00}`,
            helpText: 'Additional charge for extra time (time elapsed while in "Arrived At Pickup")',
        },
        {
            label: 'Per Km Charge',
            value: `$${booking.priceBreakUp.perKmCharge.toFixed(2) || 0.00}`,
            helpText: 'Charge for extra distance',
        },
        {
            label: 'Service Fee',
            value: `$${booking.priceBreakUp.comission.toFixed(2) || 0.00}`,
            helpText: 'Commission amount. 10% of the order amount generated by this purchase order',
        },
        {
            label: 'Total delivery cost',
            value: `$${booking.getDeliveryFee().toFixed(2) || 0.00}`,
            helpText: 'Total cost of the service',
            tag: true,
            tagColor: 'yellow',
        },
    ];

    const PriceBreakUpItem = ({
        label,
        value,
        helpText,
        tag,
        tagColor,
    }: PriceBreakUpItemProps) => (
        <Item
            span={3}
            label={(
                <Tooltip title={helpText}>
                    <Space align="center" size={4}>
                        <Text>
                            {label}
                        </Text>
                        <MdOutlineHelpOutline
                            color="#ddd"
                            size={15}
                            style={{ verticalAlign: 'middle' }}
                        />
                    </Space>
                </Tooltip>
            )}
        >
            {
                tag ? (
                    <Tag color={tagColor}>
                        {value}
                    </Tag>
                ) : (
                    value
                )
            }
        </Item>
    );

    return (
        <Card
            style={{ height: '100%', marginBottom: 0 }}
            title="Price Breakup"
            extra={(
                <Button
                    onClick={showEditPriceBreakupModal}
                    size="small"
                >
                    Edit
                </Button>
            )}
        >
            <Row justify="start" gutter={[0, 20]}>
                <Col xs={24} md={12} xl={8}>
                    <Descriptions
                        title="Product Costs"
                        className="user-table"
                    >
                        {PriceBreakUpItem({
                            label: 'Business Order Amount',
                            value: `$${booking.priceBreakUp.businessOrderAmount?.toFixed(2) || 0.00}`,
                            helpText: 'Value of the product to be delivered. Entered by the business',
                        })}
                        {PriceBreakUpItem({
                            label: `IVA (${booking.pricingDetails.taxPercentage}%)`,
                            value: `$${booking.priceBreakUp.tax?.toFixed(2) || 0.00}`,
                            helpText: 'IVA included in the business delivery fee',
                        })}
                        {PriceBreakUpItem({
                            label: 'Business Delivery Fee',
                            value: `$${booking.priceBreakUp.businessDeliveryFee?.toFixed(2) || 0.00}`,
                            helpText: 'Delivery fee (IVA included)',
                        })}
                        {PriceBreakUpItem({
                            label: 'Order Fee',
                            value: `$${booking.orderAmount.toFixed(2) || 0.00}`,
                            helpText: 'Value of the product + Delivery Fee',
                            tag: true,
                            tagColor: 'blue',
                        })}
                    </Descriptions>
                </Col>
                <Col xs={24} md={12} xl={8}>
                    <Descriptions title="Delivery Cost" className="user-table">
                        {
                            DELIVERY_PRICE_ITEMS.map((item, index) => (
                                PriceBreakUpItem({
                                    ...item,
                                })
                            ))
                        }
                    </Descriptions>
                </Col>
                <Col xs={24} md={12} xl={8}>
                    <Descriptions title="Total" className="user-table">
                        {PriceBreakUpItem({
                            label: 'Total Order Amount',
                            value: `$${booking.orderAmount.toFixed(2) || 0.00}`,
                            helpText: 'Value of the product + Delivery Fee',
                            tag: true,
                            tagColor: 'blue',
                        })}
                        {PriceBreakUpItem({
                            label: 'Total Delivery Cost',
                            value: `$${booking.getDeliveryFee().toFixed(2) || 0.00}`,
                            helpText: 'Total cost of the service',
                            tag: true,
                            tagColor: 'yellow',
                        })}
                        {PriceBreakUpItem({
                            label: 'Total',
                            value: `$${booking.getTotalPay().toFixed(2) || 0.00}`,
                            helpText: 'Total amount charged to the end customer including discounts',
                            tag: true,
                            tagColor: 'green',
                        })}
                    </Descriptions>
                </Col>
            </Row>
        </Card>
    );
};

export { PriceBreakUp };
